import { Box, Typography, useTheme } from '@mui/material';
import CustomLink from '../custom/custom-link';
import { SUBSCRIPTION_TYPE } from '@/constants/subscription-type';
import { useContext, useEffect, useState } from 'react';
import { Context } from '@/context';
import NavigationItems from '../navigation/NavigationItems';
import useConsentManager from '@/DesignSystem/hooks/useConsentManager';
import useRouterPathname from '@/DesignSystem/hooks/useRouterPathname';
import FixturesLayout from './FixturesLayout';
import StoriesLayout from './stories/StoriesLayout';
import { REDUCERS } from '@/constants/reducers';
import useIsUserPremium from '@/DesignSystem/hooks/useIsUserPremium';
import useFavoritos from '@/hooks/useFavoritos';
import useIsUserAuthenticated from '@/DesignSystem/hooks/userIsAuthenticated';

const SecondaryNavigationLayout = ({ children, isBettingSection }) => {
    // 1. useState hooks
    const [subMenuItems, setSubMenuItems] = useState([]);

    const { obtenerFavoritos } = useFavoritos();
    const isUserAuthenticated = useIsUserAuthenticated();

    // 2. useContext hooks
    const theme = useTheme();
    const { state, dispatch } = useContext(Context);
    const { user } = state;

    // 3. Custom hooks
    const pathname = useRouterPathname();
    useConsentManager(dispatch);

    const isUserPremium = useIsUserPremium();

    // 4. useMemo hooks
    const allNavigationItems = NavigationItems(user, false, 20, true);

    // 5. useEffect hooks
    useEffect(() => {
        let newItems = [];
        allNavigationItems?.forEach((item) => {
            item.subMenu?.forEach((subItem) => {
                if (pathname === subItem.path) {
                    newItems = item.subMenu;
                }
            });
        });
        setSubMenuItems(newItems);
    }, [pathname]);

    useEffect(() => {
        dispatch({ type: REDUCERS.UPDATE_URLS, payload: pathname });
    }, [pathname]);

    useEffect(() => {
        if (isUserAuthenticated) {
            // Ejecutar cuando el navegador tenga tiempo libre
            if ('requestIdleCallback' in window) {
                requestIdleCallback(() => {
                    obtenerFavoritos();
                });
            } else {
                // Fallback para navegadores que no soportan requestIdleCallback
                setTimeout(() => {
                    obtenerFavoritos();
                }, 0);
            }
        }
    }, [isUserAuthenticated]);

    return (
        <Box
            sx={{ bgcolor: 'background.default' }}
            className={`${
                user?.subscriptionType !== SUBSCRIPTION_TYPE.FREE ||
                process.env.NEXT_PUBLIC_ENV_VARIABLE === 'development'
                    ? 'analitica-premium'
                    : 'analitica-free'
            } af-main-content-${state.theme} main-content url-${pathname}`}
        >
            <Box
                display={'flex'}
                gap={2}
                sx={{
                    bgcolor: 'background.default',
                    overflowX: 'auto',
                    px: 1,
                    justifyContent: { xs: 'flex-start', md: 'center' },
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                }}
            >
                {subMenuItems
                    ?.filter((item) => item.path)
                    .map((item) => {
                        return (
                            <CustomLink
                                key={item.path}
                                href={item.path}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                useNativeLink={!isUserPremium}
                                useWrapper={true}
                            >
                                <Typography
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        my: 0.5,
                                        borderBottom:
                                            pathname === item.path
                                                ? `2px solid ${theme.palette.primary.main}`
                                                : 'none',
                                        fontSize: 14,
                                    }}
                                >
                                    {item.shortTitle}
                                </Typography>
                            </CustomLink>
                        );
                    })}
            </Box>

            {!isBettingSection && <FixturesLayout />}

            {!isBettingSection && <StoriesLayout />}

            <Box component={'main'} sx={{ pb: 2, px: 1, pt: 1 }} className="main-website">
                {children}
            </Box>
        </Box>
    );
};

export default SecondaryNavigationLayout;
